import { featureFlags } from '@seen/core/featureFlags'
import type { Editor } from '@tiptap/core'
import { type JSX, type ReactNode, useContext, useEffect, useRef } from 'react'
import { ObservationFrameEngagement } from '#src/components/rich-text-editor/ObservationNode/observationFrameEngagement.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { extractBlockLinesFromHTML } from '#src/utils/html.ts'

// Holds an observation's notes editor. In a line, an empty note is not shown
// at all, so engaging the node never changes its size: the frame offers "Add
// note", and only then does the editor appear, focused. A written note always
// shows; one of more than one line shows its first line, which opens the
// details to write the rest. In the details (`inDetails`) the editor always
// shows. Behind the structured capture flag; without it the notes editor
// always shows, as before.
export function ObservationNoteSlot({
  note,
  inDetails = false,
  children,
}: {
  note: string | null | undefined
  inDetails?: boolean
  // The note's editor, with the spacing before it.
  children: ReactNode
}): JSX.Element | null {
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
  const { openDetails, reportNoteSlot, noteRequested } = useContext(
    ObservationFrameEngagement
  )
  const lines = note ? extractBlockLinesFromHTML(note) : []
  const inLine = inlineCapture && !inDetails
  const empty = lines.length === 0
  const slotRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!inLine) {
      return
    }
    reportNoteSlot?.(empty ? 'empty' : 'filled')
    return () => reportNoteSlot?.(null)
  }, [inLine, empty, reportNoteSlot])

  // Asked for: the editor appears, and takes focus once it is in place.
  const asked = inLine && empty && !!noteRequested
  useEffect(() => {
    if (!asked) {
      return
    }
    let tries = 0
    let frame = 0
    const focus = (): void => {
      const editable = slotRef.current?.querySelector<
        HTMLElement & { editor?: Editor }
      >('[contenteditable="true"]')
      if (editable?.editor) {
        editable.editor.commands.focus('end')
      } else if (tries++ < 20) {
        frame = requestAnimationFrame(focus)
      }
    }
    focus()
    return () => cancelAnimationFrame(frame)
  }, [asked])

  if (inLine && lines.length > 1 && openDetails) {
    return (
      <span className="flex min-w-0">
        &nbsp;&nbsp;
        <button
          type="button"
          onClick={() => openDetails('note')}
          className="max-w-[18rem] min-w-0 truncate text-left hover:underline focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:outline-hidden"
        >
          {lines[0]}…
        </button>
      </span>
    )
  }

  if (inLine && empty && !noteRequested) {
    return null
  }

  return (
    <span ref={slotRef} data-observation-note="" className="flex min-w-0 grow">
      {children}
    </span>
  )
}
